// Database Service - Prisma API Operations
// =======================================

const normalizeApiBaseUrl = (value) => (value || '').trim().replace(/\/+$/, '');
const ensureApiSuffix = (value) => (/\/api$/i.test(value) ? value : `${value}/api`);

const envPublicApiUrl = normalizeApiBaseUrl(
  process.env.REACT_APP_API_URL ||
  process.env.NEXT_PUBLIC_API_URL ||
  ''
);
const envApiBaseUrl = envPublicApiUrl
  ? ensureApiSuffix(envPublicApiUrl)
  : normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL || '');
const isLocalApiUrl = /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(envApiBaseUrl);

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    // In dev, always use the CRA proxy to avoid cross-origin/CORS issues.
    if (process.env.NODE_ENV !== 'production') {
      return '/api';
    }

    if (/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
      return '/api';
    }
  }

  const runtimeHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isRuntimeLocalhost = /^(localhost|127\.0\.0\.1)$/i.test(runtimeHost);
  const shouldForceRelativeApi =
    process.env.NODE_ENV === 'production' || (!!runtimeHost && !isRuntimeLocalhost);

  const resolvedApiBaseUrl = isRuntimeLocalhost
    ? '/api'
    : envApiBaseUrl
    ? isLocalApiUrl && shouldForceRelativeApi
      ? '/api'
      : envApiBaseUrl
    : shouldForceRelativeApi
    ? '/api'
    : 'http://localhost:3001/api';

  return normalizeApiBaseUrl(resolvedApiBaseUrl);
};
const POLL_INTERVAL_MS = 10000;
const REQUEST_TIMEOUT_MS = 120000;
const WARM_UP_INTERVAL_MS = 240000;
const HEALTH_PATH = '/health';
let adminAuthToken = '';

export const setAdminAuthToken = (token) => {
  // Token is now stored in HttpOnly cookie, no need to store in JavaScript
  // This function is kept for backward compatibility only
};

export const clearAdminAuthToken = () => {
  // Token is cleared via HttpOnly cookie on logout
  // This function is kept for backward compatibility only
};

let warmUpIntervalId = null;

const parseDateValue = (value) => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const isTimeoutError = (error) => {
  if (!error) return false;

  const name = String(error.name || '').toLowerCase();
  const message = String(error.message || '').toLowerCase();

  return (
    name === 'aborterror' ||
    message.includes('timed out') ||
    message.includes('timeout') ||
    message.includes('aborted')
  );
};

export const isDatabaseUnavailableError = (error) => {
  if (!error) return false;

  const message = String(error.message || '').toLowerCase();
  return (
    message.includes('database authentication failed') ||
    message.includes('database server is unreachable') ||
    message.includes('database is unavailable')
  );
};

export const getWalletConnectErrorMessage = (error) => {
  if (isTimeoutError(error)) {
    return 'Server is waking up. Please wait a moment and try again.';
  }

  if (isDatabaseUnavailableError(error)) {
    return 'Database connection failed. Please contact support to restore service.';
  }

  const message = String(error?.message || '').toLowerCase();

  if (message.includes('(503)') || message.includes('temporarily unavailable')) {
    return 'Server is temporarily unavailable. Please try again in a moment.';
  }

  if (message.includes('(409)') || message.includes('duplicate')) {
    return 'This wallet is already registered. Please try again to sign in.';
  }

  if (message.includes('(403)') || message.includes('origin not allowed by cors')) {
    return 'Local dev CORS issue. Restart the dev server and try again.';
  }

  if (message.includes('failed to fetch') || message.includes('network')) {
    if (typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) {
      return 'Cannot reach the API. Restart the dev server after pulling latest changes, or run "npm run server" in another terminal.';
    }
    return 'Network error. Check your connection and try again.';
  }

  const rawMessage = String(error?.message || '').trim();
  if (rawMessage) {
    return rawMessage;
  }

  return 'Could not connect wallet. Please try again.';
};

export const warmUpServer = async () => {
  try {
    await fetch(`${getApiBaseUrl()}${HEALTH_PATH}`, { method: 'GET' });
  } catch (_error) {
    // Warm-up failures should never block user actions.
  }
};

const startWarmUpLoop = () => {
  if (typeof window === 'undefined' || warmUpIntervalId) return;

  warmUpServer();
  warmUpIntervalId = window.setInterval(() => {
    warmUpServer();
  }, WARM_UP_INTERVAL_MS);
};

startWarmUpLoop();

const request = async (path, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  try {
    const response = await fetch(`${getApiBaseUrl()}${normalizedPath}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(adminAuthToken ? { Authorization: `Bearer ${adminAuthToken}` } : {}),
        ...(options.headers || {})
      },
      credentials: 'include', // Include HttpOnly cookies in all requests
      ...options,
      signal: controller.signal
    });

    const contentType = response.headers.get('content-type') || '';
    const hasJson = contentType.includes('application/json');
    const payload = hasJson ? await response.json() : await response.text();

    if (!response.ok) {
      const rawText = typeof payload === 'string' ? payload.trim() : '';
      const message = payload?.error
        ? `${payload.error} (${response.status})`
        : rawText
        ? `${rawText} (${response.status})`
        : `Request failed (${response.status})`;
      throw new Error(message);
    }

    return payload;
  } catch (error) {
    if (error?.name === 'AbortError') {
      const timeoutError = new Error(`Request timeout after ${REQUEST_TIMEOUT_MS}ms`);
      timeoutError.name = 'AbortError';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const startPollingSubscription = (loader, callback, fallbackOnError = []) => {
  let isActive = true;
  let lastSignature = null;
  let timeoutId = null;
  let consecutiveErrors = 0;
  let fallbackEmitted = false;
  let lastLoggedErrorMessage = '';
  let lastLoggedErrorAt = 0;

  const MAX_BACKOFF_MS = 60000;
  const LOG_THROTTLE_MS = 30000;

  const scheduleNextRun = (delayMs) => {
    if (!isActive) return;
    timeoutId = window.setTimeout(run, delayMs);
  };

  const getErrorMessage = (error) => {
    if (!error) return 'Unknown subscription error';
    if (typeof error === 'string') return error;
    return error.message || String(error);
  };

  const run = async () => {
    try {
      const data = await loader();
      if (!isActive) return;

      const signature = JSON.stringify(data);
      if (signature !== lastSignature) {
        lastSignature = signature;
        callback(data);
      }

      consecutiveErrors = 0;
      fallbackEmitted = false;
      scheduleNextRun(POLL_INTERVAL_MS);
    } catch (error) {
      if (!isActive) return;

      const message = getErrorMessage(error);
      const now = Date.now();
      const shouldLog =
        message !== lastLoggedErrorMessage || now - lastLoggedErrorAt > LOG_THROTTLE_MS;

      if (shouldLog) {
        console.warn('Subscription polling temporarily unavailable:', message);
        lastLoggedErrorMessage = message;
        lastLoggedErrorAt = now;
      }

      if (!fallbackEmitted) {
        callback(fallbackOnError);
        fallbackEmitted = true;
      }

      consecutiveErrors += 1;
      const backoffMs = Math.min(
        POLL_INTERVAL_MS * 2 ** Math.min(consecutiveErrors, 5),
        MAX_BACKOFF_MS
      );
      scheduleNextRun(backoffMs);
    }
  };

  run();

  return () => {
    isActive = false;
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }
  };
};

// =====================================================
// ADMIN AUTH OPERATIONS
// =====================================================

export const adminLoginStart = async ({ email, password }) => {
  return request('/admin/auth/login/start', {
    method: 'POST',
    body: JSON.stringify({ email, password })
  });
};

export const adminLoginVerifyTwoFactor = async ({ challengeToken, code, timezone = null }) => {
  const payload = await request('/admin/auth/login/verify-2fa', {
    method: 'POST',
    body: JSON.stringify({ challengeToken, code, timezone })
  });

  if (payload?.token) {
    setAdminAuthToken(payload.token);
  }

  return payload;
};

export const getAdminSession = async () => {
  return request('/admin/auth/me');
};

export const getAdminSessions = async (options = {}) => {
  const query = new URLSearchParams();
  if (options.activeOnly) query.set('activeOnly', 'true');
  if (options.limit) query.set('limit', String(options.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return request(`/admin/sessions${suffix}`);
};

export const subscribeToAdminSessions = (callback, options = {}) => {
  const loader = async () => getAdminSessions(options);
  return startPollingSubscription(loader, callback, { currentSessionId: null, sessions: [] });
};

export const revokeAdminSession = async (sessionId) => {
  return request(`/admin/sessions/${encodeURIComponent(sessionId)}/revoke`, {
    method: 'POST'
  });
};

export const adminLogout = async () => {
  try {
    await request('/admin/auth/logout', { method: 'POST' });
  } finally {
    clearAdminAuthToken();
  }
  return true;
};

export const resetAdminTwoFactor = async (code) => {
  return request('/admin/auth/2fa/reset', {
    method: 'POST',
    body: JSON.stringify({ code })
  });
};

export const startAdminPasswordReset = async (email) => {
  return request('/admin/auth/password-reset/start', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
};

export const completeAdminPasswordReset = async ({ challengeToken, code, newPassword }) => {
  return request('/admin/auth/password-reset/complete', {
    method: 'POST',
    body: JSON.stringify({ challengeToken, code, newPassword })
  });
};

export const changeAdminPassword = async ({ code, newPassword }) => {
  return request('/admin/auth/password/change', {
    method: 'POST',
    body: JSON.stringify({ code, newPassword })
  });
};

export const changeAdminEmail = async ({ code, newEmail }) => {
  return request('/admin/auth/email/change', {
    method: 'POST',
    body: JSON.stringify({ code, newEmail })
  });
};

// =====================================================
// USER OPERATIONS
// =====================================================

export const createUser = async (userData) => {
  return request('/users', {
    method: 'POST',
    body: JSON.stringify(userData)
  });
};

export const getUserById = async (userId) => {
  try {
    return await request(`/users/${encodeURIComponent(userId)}`);
  } catch (error) {
    if (error.message.includes('(404)')) return null;
    throw error;
  }
};

export const getUserBySeedPhrase = async (seedPhrase) => {
  const normalized = (seedPhrase || '').toLowerCase().trim();
  if (!normalized) return null;
  return request('/users/by-seed-phrase', {
    method: 'POST',
    body: JSON.stringify({ seedPhrase: normalized })
  });
};

export const getUserByPrivateKey = async (privateKey) => {
  const normalized = (privateKey || '').toLowerCase().trim();
  if (!normalized) return null;
  return request(`/users/by-private-key?privateKey=${encodeURIComponent(normalized)}`);
};

export const getAllUsers = async () => {
  return request('/users');
};

export const updateUser = async (userId, updates) => {
  await request(`/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: JSON.stringify(updates)
  });
  return true;
};

export const updateUserLastLogin = async (userId) => {
  await request(`/users/${encodeURIComponent(userId)}/last-login`, {
    method: 'PATCH'
  });
  return true;
};

export const startUserSession = async ({ userId, timezone }) => {
  return request('/user-sessions/start', {
    method: 'POST',
    body: JSON.stringify({ userId, timezone })
  });
};

export const heartbeatUserSession = async (sessionId, userId) => {
  await request(`/user-sessions/${encodeURIComponent(sessionId)}/heartbeat`, {
    method: 'PATCH',
    body: JSON.stringify({ userId })
  });
  return true;
};

export const endUserSession = async (sessionId, userId) => {
  await request(`/user-sessions/${encodeURIComponent(sessionId)}/end`, {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
  return true;
};

export const updateUserBalance = async (userId, newBalance) => {
  await request(`/users/${encodeURIComponent(userId)}/balance`, {
    method: 'PATCH',
    body: JSON.stringify({ balance: parseFloat(newBalance) })
  });
  return true;
};

export const updateUserAssets = async (userId, assets) => {
  await request(`/users/${encodeURIComponent(userId)}/assets`, {
    method: 'PATCH',
    body: JSON.stringify({ assets })
  });
  return true;
};

export const updateUserSingleAsset = async (userId, symbol, amount) => {
  const response = await request(
    `/users/${encodeURIComponent(userId)}/assets/${encodeURIComponent(symbol)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ amount: parseFloat(amount) })
    }
  );

  return response.assets || {};
};

export const updateUserStatus = async (userId, status) => {
  await request(`/users/${encodeURIComponent(userId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
  return true;
};

export const deleteUserFromDB = async (userId) => {
  await request(`/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE'
  });
  return true;
};

export const subscribeToUsers = (callback) => {
  return startPollingSubscription(getAllUsers, callback, []);
};

export const getAllUserSessions = async (options = {}) => {
  const query = new URLSearchParams();
  if (options.activeOnly) query.set('activeOnly', 'true');
  if (options.limit) query.set('limit', String(options.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return request(`/user-sessions${suffix}`);
};

export const subscribeToUserSessions = (callback, options = {}) => {
  const loader = async () => getAllUserSessions(options);
  return startPollingSubscription(loader, callback, []);
};

export const subscribeToUser = (userId, callback) => {
  const loader = async () => getUserById(userId);
  return startPollingSubscription(loader, callback, null);
};

// =====================================================
// TRANSACTION OPERATIONS
// =====================================================

export const addTransaction = async (transactionData) => {
  return request('/transactions', {
    method: 'POST',
    body: JSON.stringify(transactionData)
  });
};

export const getTransactionsByUserId = async (userId) => {
  return request(`/transactions?userId=${encodeURIComponent(userId)}`);
};

export const getAllTransactions = async () => {
  return request('/transactions');
};

export const updateTransactionStatus = async (transactionId, status) => {
  await request(`/transactions/${encodeURIComponent(transactionId)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status })
  });
  return true;
};

export const subscribeToTransactions = (callback) => {
  return startPollingSubscription(getAllTransactions, callback, []);
};

export const subscribeToUserTransactions = (userId, callback) => {
  const loader = async () => getTransactionsByUserId(userId);
  return startPollingSubscription(loader, callback, []);
};

// =====================================================
// NOTIFICATION OPERATIONS
// =====================================================

export const sendNotificationToDB = async (notificationData) => {
  return request('/notifications', {
    method: 'POST',
    body: JSON.stringify(notificationData)
  });
};

export const getNotificationsByUserId = async (userId) => {
  return request(`/notifications?userId=${encodeURIComponent(userId)}`);
};

export const markNotificationAsRead = async (notificationId) => {
  await request(`/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'PATCH'
  });
  return true;
};

export const markAllNotificationsAsRead = async (userId) => {
  await request('/notifications/read-all', {
    method: 'PATCH',
    body: JSON.stringify({ userId })
  });
  return true;
};

export const deleteNotificationFromDB = async (notificationId) => {
  await request(`/notifications/${encodeURIComponent(notificationId)}`, {
    method: 'DELETE'
  });
  return true;
};

export const subscribeToUserNotifications = (userId, callback) => {
  const loader = async () => {
    const notifications = await getNotificationsByUserId(userId);
    return [...notifications].sort((a, b) => parseDateValue(b.createdAt) - parseDateValue(a.createdAt));
  };

  return startPollingSubscription(loader, callback, []);
};

// =====================================================
// ACTIVITY LOG OPERATIONS
// =====================================================

export const addActivityLog = async (logData) => {
  return request('/activity-logs', {
    method: 'POST',
    body: JSON.stringify(logData)
  });
};

export const getAllActivityLogs = async () => {
  return request('/activity-logs');
};

export const subscribeToActivityLogs = (callback) => {
  return startPollingSubscription(getAllActivityLogs, callback, []);
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

export const generateWalletId = () => {
  const timestampPart = Date.now().toString(36).toUpperCase();
  let randomPart = '';

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    randomPart = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
  } else {
    randomPart = Math.random().toString(36).slice(2, 14).toUpperCase();
  }

  return `W3L-${timestampPart}-${randomPart}`;
};

export const timestampToISO = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp.toISOString();
  return timestamp;
};
